/**
 * Placement Test Service — Builds a 14-question diagnostic test
 * for Fortgeschrittene (Mittel) and Erfahrene (Schwer).
 *
 * Selects 1 question per chapter at the appropriate difficulty level,
 * falling back to the next-closest difficulty if the target pool is empty.
 */
import type { Question} from '../../shared/types';
import { Difficulty } from '../../shared/types';
import { ApiClient } from './apiClient';
import { CHAPTERS } from '../constants/chapters';

export type PlacementDifficulty = 'fortgeschritten' | 'erfahren';

export interface PlacementTestResult {
  questions: Question[];
  difficulty: PlacementDifficulty;
  chapterCoverage: number; // How many chapters are covered
}

/**
 * Build a placement test: 1 question per chapter (14 total).
 * - fortgeschritten → Mittel difficulty
 * - erfahren → Schwer difficulty
 */
export function buildPlacementTest(level: PlacementDifficulty): PlacementTestResult {
  const targetDifficulty = level === 'erfahren' ? Difficulty.HARD : Difficulty.MEDIUM;

  // Fallback order: if target difficulty has no questions for a chapter
  const fallbackOrder: Record<string, Difficulty[]> = {
    [Difficulty.MEDIUM]: [Difficulty.EASY, Difficulty.HARD],
    [Difficulty.HARD]: [Difficulty.MEDIUM, Difficulty.EASY],
  };

  const selected: Question[] = [];

  for (const chapter of CHAPTERS) {
    const allForChapter = ApiClient.getQuestionsForChapter(chapter.id);
    
    // Try target difficulty first
    let candidates = allForChapter.filter(q => q.difficulty === targetDifficulty);
    
    // Fallback if no questions at target difficulty
    if (candidates.length === 0) {
      for (const fallback of fallbackOrder[targetDifficulty]) {
        candidates = allForChapter.filter(q => q.difficulty === fallback);
        if (candidates.length > 0) break;
      }
    }

    // Pick 1 random question from candidates
    if (candidates.length > 0) {
      const randomIndex = Math.floor(Math.random() * candidates.length);
      selected.push(candidates[randomIndex]);
    }
  }

  return {
    questions: selected,
    difficulty: level,
    chapterCoverage: selected.length,
  };
}
